// src/services/verification/verificationService.js

import { auth, db } from '../../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
  setDoc,
  FieldValue,
} from 'firebase/firestore';

export const VerificationStatus = {
  NOT_STARTED: 'notStarted',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

class VerificationService {
  // ── Singleton ──────────────────────────────────────────────────────────────

  constructor() {
    this._auth = auth;
    this._db = db;
  }

  static getInstance() {
    if (!VerificationService._instance) {
      VerificationService._instance = new VerificationService();
    }
    return VerificationService._instance;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CHECK METHODS
  // ─────────────────────────────────────────────────────────────────────────

  async hasUserSoldBefore() {
    const user = this._auth.currentUser;
    if (!user) return false;

    try {
      const q = query(
        collection(this._db, 'listings'),
        where('uid', '==', user.uid),
        limit(1)
      );
      const querySnap = await getDocs(q);
      return !querySnap.empty;
    } catch (error) {
      console.error('Error checking seller history:', error);
      return false;
    }
  }

  async hasCompletedVerification() {
    const user = this._auth.currentUser;
    if (!user) return false;

    try {
      const docRef = doc(this._db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return false;

      const data = docSnap.data();
      const verification = data.verification;

      // Check if verification exists AND is approved AND isVerifiedSeller is true
      if (verification &&
          verification.status === 'approved' &&
          verification.isVerifiedSeller === true) {
        return true;
      }

      // Also check the direct field in user document
      if (data.isVerifiedSeller === true) {
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking verification:', error);
      return false;
    }
  }

  async hasPendingVerification() {
    const user = this._auth.currentUser;
    if (!user) return false;

    try {
      const docRef = doc(this._db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return false;

      const data = docSnap.data();
      const verification = data.verification;

      return verification && verification.status === 'pending';
    } catch (error) {
      console.error('Error checking pending verification:', error);
      return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GET STATUS
  // ─────────────────────────────────────────────────────────────────────────

  async getVerificationStatus() {
    const user = this._auth.currentUser;
    if (!user) return VerificationStatus.NOT_STARTED;

    try {
      const docRef = doc(this._db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return VerificationStatus.NOT_STARTED;

      const data = docSnap.data();
      const verification = data.verification;

      if (!verification) return VerificationStatus.NOT_STARTED;

      const status = verification.status;

      switch (status) {
        case 'approved':
          return VerificationStatus.APPROVED;
        case 'pending':
          return VerificationStatus.PENDING;
        case 'rejected':
          return VerificationStatus.REJECTED;
        default:
          return VerificationStatus.NOT_STARTED;
      }
    } catch (error) {
      console.error('Error getting verification status:', error);
      return VerificationStatus.NOT_STARTED;
    }
  }

  async getVerificationData() {
    const user = this._auth.currentUser;
    if (!user) return null;

    try {
      const docRef = doc(this._db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return null;

      const data = docSnap.data();
      return data.verification || null;
    } catch (error) {
      console.error('Error getting verification data:', error);
      return null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ADMIN METHODS
  // ─────────────────────────────────────────────────────────────────────────

  async getPendingVerifications() {
    try {
      const q = query(
        collection(this._db, 'sellerVerifications'),
        where('status', '==', 'pending'),
        orderBy('submittedAt', 'desc')
      );
      const querySnap = await getDocs(q);
      return querySnap.docs;
    } catch (error) {
      console.error('Error getting pending verifications:', error);
      return [];
    }
  }

  async approveVerification(verificationId) {
    try {
      const verificationRef = doc(this._db, 'sellerVerifications', verificationId);
      const verificationDoc = await getDoc(verificationRef);

      if (!verificationDoc.exists()) return false;

      const verificationData = verificationDoc.data();
      const userId = verificationData?.userId;

      if (!userId) return false;

      // Update the sellerVerifications document
      await updateDoc(verificationRef, {
        status: 'approved',
        isVerifiedSeller: true,
        reviewedAt: FieldValue.serverTimestamp(),
        reviewedBy: this._auth.currentUser?.uid,
      });

      // Update the user profile
      await updateDoc(doc(this._db, 'users', userId), {
        'verification.status': 'approved',
        'verification.isVerifiedSeller': true,
        'verification.reviewedAt': FieldValue.serverTimestamp(),
        isVerifiedSeller: true,
        verifiedAt: FieldValue.serverTimestamp(),
      });

      return true;
    } catch (error) {
      console.error('Error approving verification:', error);
      return false;
    }
  }

  async rejectVerification(verificationId, reason) {
    try {
      const verificationRef = doc(this._db, 'sellerVerifications', verificationId);
      const verificationDoc = await getDoc(verificationRef);

      if (!verificationDoc.exists()) return false;

      const verificationData = verificationDoc.data();
      const userId = verificationData?.userId;

      if (!userId) return false;

      // Update the sellerVerifications document
      await updateDoc(verificationRef, {
        status: 'rejected',
        isVerifiedSeller: false,
        rejectionReason: reason,
        reviewedAt: FieldValue.serverTimestamp(),
        reviewedBy: this._auth.currentUser?.uid,
      });

      // Update the user profile
      await updateDoc(doc(this._db, 'users', userId), {
        'verification.status': 'rejected',
        'verification.isVerifiedSeller': false,
        'verification.rejectionReason': reason,
        'verification.reviewedAt': FieldValue.serverTimestamp(),
        isVerifiedSeller: false,
      });

      return true;
    } catch (error) {
      console.error('Error rejecting verification:', error);
      return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GET METHODS
  // ─────────────────────────────────────────────────────────────────────────

  async getVerificationById(verificationId) {
    try {
      const docRef = doc(this._db, 'sellerVerifications', verificationId);
      const docSnap = await getDoc(docRef);
      return docSnap;
    } catch (error) {
      console.error('Error getting verification:', error);
      return null;
    }
  }

  async getUserVerifications(userId) {
    try {
      const q = query(
        collection(this._db, 'sellerVerifications'),
        where('userId', '==', userId),
        orderBy('submittedAt', 'desc')
      );
      const querySnap = await getDocs(q);
      return querySnap.docs;
    } catch (error) {
      console.error('Error getting user verifications:', error);
      return [];
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // SUBMIT VERIFICATION
  // ─────────────────────────────────────────────────────────────────────────

  async submitVerification(data) {
    const user = this._auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const verificationId = doc(collection(this._db, 'sellerVerifications')).id;

      // Create verification document
      await setDoc(doc(this._db, 'sellerVerifications', verificationId), {
        ...data,
        userId: user.uid,
        status: 'pending',
        isVerifiedSeller: false,
        submittedAt: FieldValue.serverTimestamp(),
        updatedAt: FieldValue.serverTimestamp(),
      });

      // Update user profile with verification status
      await updateDoc(doc(this._db, 'users', user.uid), {
        'verification.status': 'pending',
        'verification.isVerifiedSeller': false,
        'verification.submittedAt': FieldValue.serverTimestamp(),
        verificationId: verificationId,
      });

      return { success: true, verificationId };
    } catch (error) {
      console.error('Error submitting verification:', error);
      return { success: false, error: error.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DISPOSE
  // ─────────────────────────────────────────────────────────────────────────

  dispose() {
    // Clean up any subscriptions if needed
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const verificationService = VerificationService.getInstance();
export { verificationService };
export default verificationService;